<script lang="ts">
export default {
  name: 'AccountSelectModal',
}
</script>
<script setup lang="ts">
import {computed, defineProps, ref} from 'vue'
import { KkButton, KkModal } from 'src/biz_spa/components/base'
import { KkSearchForm } from 'src/biz_spa/components/ui'
import { KkAccountInfoLabel } from 'src/biz_spa/components/domains/sns'

/**
 * Props
 */
type Picture = {
  url: string
}
type Account = {
  id: string
  username: string
  picture: Picture
  name: string
}
type Props = {
  isShownAccountSelectModal: boolean
  accounts: Account[]
}
const props = defineProps<Props>()

/**
 * Emits
 */
type Emits = {
  (key: 'onCancel'): void
  (key: 'onSubmit', id: string): void
}
const emits = defineEmits<Emits>()

/**
 * Ref
 */
const keyword = ref('')
const selectedAccountId = ref<string>(props.accounts[0].id)

/**
 * Methods
 */
const handleSubmit = () => {
  emits('onSubmit', selectedAccountId.value)
}

const accounts = computed(() => {
  return props.accounts.filter(account => {
    return account.name.includes(keyword.value)
  })
})
</script>

<template>
  <KkModal :is-visible="props.isShownAccountSelectModal">
    <div class="content__header">
      {{ $t('pages.companies.instagramHashtag.selectAccountModal.title') }}
    </div>
    <div class="content__body">
      <div class="searchFormWrapper">
        <KkSearchForm v-model="keyword" />
      </div>
      <div v-for="account in accounts" :key="account.id" class="accounts">
        <div class="account">
          <input
            :id="account.id.toString()"
            v-model="selectedAccountId"
            name="selectedAccountId"
            type="radio"
            class="radio__input"
            :value="account.id"
            :checked="selectedAccountId === account.id"
          >
          <label
            v-if="account"
            :for="account.id.toString()"
            class="radio__label"
          >
            <KkAccountInfoLabel
              :image-url="account.picture.url"
              sns-type="instagram"
              :name="account.name"
              :page-id="account.username"
            />
          </label>
        </div>
      </div>
    </div>
    <div class="content__footer">
      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton color="secondary" outlined :text="$t('labels.cancel')" @click="emits('onCancel')" />
        </div>
        <div class="buttonGroup__item">
          <KkButton color="primary" :text="$t('labels.confirm')" @click="handleSubmit" />
        </div>
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/var';
.content {
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 20px 32px;
    border-bottom: 1px solid $color-base-c;
    font-size: 16px;
    line-height: 1.5;
  }
  &__body {
    height: 50vh;
    padding: 32px;
    overflow-y: scroll;
  }
  &__footer {
    padding: 32px;
    border-top: 1px solid $color-base-c;
    display: flex;
    justify-content: flex-end;
    @media screen and (max-width: 768px) {
      display: block;
    }
  }
}
.buttonGroup {
  display: flex;
  flex-grow: 0;
  &__item {
    margin: 8px;
  }
  @media screen and (max-width: 768px) {
    justify-content: flex-end;
  }
  @media screen and (max-width: 340px) {
    justify-content: center;
  }
}
.searchFormWrapper {
  width: 240px;
  margin-left: auto;
}
.accounts {
  margin-top: 16px;
}
.account {
  display: flex;
  align-items: center;
  border: 1px solid $color-base-c;
  border-radius: 4px;
  padding: 16px;
  &:not(:last-child) {
    margin-bottom: 4px;
  }
}
.radio {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  list-style-type: none;
  &__input {
    display: none;
    &:checked + .radio__label {
      &::before {
        background-color: $color-accent-form;
      }
    }
  }
  &__label {
    display: inline-block;
    position: relative;
    padding-left: 24px;
    font-size: 14px;
    line-height: 16px;
    cursor: pointer;
    transition: .2s;
    &::before {
      position: absolute;
      top: 10px;
      left: 0;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: $color-base-h;
      content: "";
      transition: .2s;
    }
    &::after {
      position: absolute;
      top: 15px;
      left: 4px;
      transform: rotate(-45deg);
      width: 8px;
      height: 4px;
      border-left: 2px solid #FFF;
      border-bottom: 2px solid #FFF;
      content: "";
    }
  }
}
</style>
